import React, { Dispatch, SetStateAction, use, useMemo, useState } from 'react';
import { isDeepEqual } from '@fabric-ui/utils';
import { useNavigationBlocker } from '@hooks/useNavigationBlocker';
import { EntityBuildingBlock, EntityWeight, GetRiskScoringConfigResponse, ProjectionToBaseScore } from './types';
import { transformRiskScoringConfig } from './utils';

export interface RiskScoreContextType {
  entityWeights: EntityWeight[];
  setEntityWeights: Dispatch<SetStateAction<EntityWeight[]>>;
  buildingBlocks: EntityBuildingBlock[];
  editableBaseScores: ProjectionToBaseScore[];
  setEditableBaseScores: Dispatch<SetStateAction<ProjectionToBaseScore[]>>;
  initialWeights: EntityWeight[];
  initialBaseScores: ProjectionToBaseScore[];
  isDirty: boolean;
  riskScoreProjectionName: string;
  data: GetRiskScoringConfigResponse;
}

const RiskScoreContext = React.createContext<RiskScoreContextType | null>(null);

export interface RiskScoreProviderProps {
  children: React.ReactNode;
  data: GetRiskScoringConfigResponse;
  riskScoreProjectionName: string;
}

export const RiskScoreProvider = ({ children, data, riskScoreProjectionName }: RiskScoreProviderProps) => {
  const { entityWeights: initialWeights, buildingBlocks } = useMemo(
    () => transformRiskScoringConfig(data, riskScoreProjectionName),
    [data, riskScoreProjectionName]
  );
  const initialBaseScores = data.projectionToBaseScores;

  const [entityWeights, setEntityWeights] = useState<EntityWeight[]>(initialWeights);
  const [editableBaseScores, setEditableBaseScores] = useState<ProjectionToBaseScore[]>(initialBaseScores);

  const isDirty = useMemo(() => !isDeepEqual(entityWeights, initialWeights), [entityWeights, initialWeights]);
  useNavigationBlocker({ isDirty });

  const value = useMemo(
    () => ({
      entityWeights,
      setEntityWeights,
      buildingBlocks,
      editableBaseScores,
      setEditableBaseScores,
      initialWeights,
      initialBaseScores,
      isDirty,
      riskScoreProjectionName,
      data,
    }),
    [entityWeights, buildingBlocks, editableBaseScores, initialWeights, initialBaseScores, isDirty, riskScoreProjectionName, data]
  );

  return <RiskScoreContext value={value}>{children}</RiskScoreContext>;
};

export const useRiskScoreContext = (): RiskScoreContextType => {
  const ctx = use(RiskScoreContext);
  if (!ctx) {
    throw new Error('useRiskScoreContext must be used within a RiskScoreProvider');
  }
  return ctx;
};
